import { Injectable } from '@angular/core';
import { IActivityConfiguration } from '../interfaces/IActivityComponent';
import { ActivityType } from '../../model/pipeline/activitytype';
import { ActivityMetaModel } from './activity-meta-model';
import { KustoActivity } from 'app/model/pipeline/kusto.activity';
import { GlobalVariables } from 'app/services/globals';
import { DataflowRefreshActivity } from 'app/model/pipeline/dataflow-refresh.activity';
import { ValidationActivity } from 'app/model/pipeline/validation.activity';
import { ScopeActivity } from 'app/model/pipeline/scope.activity';
import { Metamodel, 
    SynapseScopeJobDefinitionActivity, 
    TridentNotebookActivity, 
    KqlActivity, 
    TridentSparkJobDefinitionActivity, 
    InvokeSSISPackageActivity, 
    InvokeCopyJobActivity, 
    MicrosoftTeamsActivity, 
    Office365EmailActivity,
    InvokeDataQualityActivity,
    InvokeDataBuildToolJobActivity,
    FabricApprovalActivity} from 'shell-models-lib';
import { ApiIntegrationActivity } from 'app/model/pipeline/api-integration.activity';
import { FabricActionsActivity } from 'app/model/pipeline/fabric-actions.activity';
import { BusinessActionsActivity } from 'app/model/pipeline/business-actions.activity';

import { StoredProcedureActivity } from 'app/model/pipeline/storedprocedure.activity';
import { InvokeADFPipelineActivity, AzureBlobStorageActivity, AzureDevOpsActivity, EmailActivity, TeamsActivity } from 'app/model/pipeline/extensible.activity';
import { WebHookActivity } from 'app/model/pipeline/webhook.activity';
import { SwitchActivity } from 'app/model/pipeline/switch.activity';
import { AzureMLExecutePipelineActivity } from 'app/model/pipeline/azure-ml-execute-pipeline.activity';
import { ScopeOnSynapseUtils } from 'app/extension/api/scope-on-synapse-utils';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { DeleteActivity } from 'app/model/pipeline/delete.activity';
import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { ExecutePipelineActivity, ExecutePipelineActivityDuplicate } from 'app/model/pipeline/execute-pipeline.activity';
import { ScriptActivity } from 'app/model/pipeline/script.activity';
import { WebActivity } from 'app/model/pipeline/web.activity';
import { SemanticModelRefreshActivity } from 'app/model/pipeline/semantic-model-refresh.activity';
import { SetVariableActivity } from 'app/model/pipeline/variables.activity';
import { AppendVariableActivity } from 'app/model/pipeline/variable-append.activity';
import { FilterActivity } from 'app/model/pipeline/filter.activity';
import { IfConditionActivity } from 'app/model/pipeline/if-condition.activity';
import { ForEachActivity } from 'app/model/pipeline/foreach.activity';
import { UntilActivity } from 'app/model/pipeline/until.activity';
import { NotebookActivity } from 'app/model/pipeline/notebook.activity';
import { SparkJarActivity } from 'app/model/pipeline/spark-jar.activity';
import { SparkPythonActivity } from 'app/model/pipeline/spark-python.activity';
import { JobActivity } from 'app/model/pipeline/job.activity';
import { AzureFunctionActivity } from 'app/model/pipeline/azure-function.activity';
import { CustomActivity } from 'app/model/pipeline/custom.activity';
import { WaitActivity } from 'app/model/pipeline/wait.activity';
import { FailActivity } from 'app/model/pipeline/fail.activity';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { AzureHDInsightActivity } from 'app/model/pipeline/azure-hdinsight.activity';
import { EmailActivityConfiguration, TeamsActivityConfiguration } from '../component/activities/extensible-activity/extensible-activity-configuration';
import { ExecutePipelineActivityConfiguration, ExecutePipelineActivityDuplicateConfiguration } from '../component/activities/execute-pipeline-activity/execute-pipeline-activity-configuration';
import { LakehouseMaintenanceActivity } from 'app/model/pipeline/lakehouse-maintenance.activity';
import { RefreshSqlEndpointActivity } from 'app/model/pipeline/refreshsqlendpoint.activity';
import { RefreshMaterializedLakeViewActivity } from 'app/model/pipeline/refresh-materialized-lake-view.activity';
import { AzureKeyVaultActivity } from 'app/model/pipeline/akv.activity';
import { AirflowActivity } from 'app/model/pipeline/airflow.activity';

export const tridentActivityOrder = [
    InvokeCopyJobActivity.MODEL,
    CopyActivity.MODEL,
    InvokeDataBuildToolJobActivity.MODEL,
    InvokeDataQualityActivity.MODEL,
    DataflowRefreshActivity.MODEL,
    DeleteActivity.MODEL,
    TridentSparkJobDefinitionActivity.MODEL,
    LookupActivity.MODEL,
    GetMetadataActivity.MODEL,
    ValidationActivity.MODEL,
    IfConditionActivity.MODEL,
    SwitchActivity.MODEL,
    FilterActivity.MODEL,
    WaitActivity.MODEL,
    ForEachActivity.MODEL,
    UntilActivity.MODEL,
    SetVariableActivity.MODEL,
    AppendVariableActivity.MODEL,
    FailActivity.MODEL,
    ExecutePipelineActivityDuplicate.MODEL,
    ExecutePipelineActivity.MODEL,
    InvokeSSISPackageActivity.MODEL,
    WebActivity.MODEL,
    ApiIntegrationActivity.MODEL,
    FabricActionsActivity.MODEL,
    BusinessActionsActivity.MODEL,
    AirflowActivity.MODEL,
    WebHookActivity.MODEL,
    AzureDevOpsActivity.MODEL,
    SemanticModelRefreshActivity.MODEL,
    AzureBlobStorageActivity.MODEL,
    Office365EmailActivity.MODEL,
    MicrosoftTeamsActivity.MODEL,
    EmailActivity.MODEL,
    TeamsActivity.MODEL,
    TridentNotebookActivity.MODEL,
    ScriptActivity.MODEL,
    StoredProcedureActivity.MODEL,
    KqlActivity.MODEL,
    ScopeActivity.MODEL,
    AzureMLExecutePipelineActivity.MODEL,
    NotebookActivity.MODEL,
    SparkJarActivity.MODEL,
    SparkPythonActivity.MODEL,
    AzureFunctionActivity.MODEL,
    JobActivity.MODEL,
    AzureHDInsightActivity.MODEL,
    CustomActivity.MODEL,
    FabricApprovalActivity.MODEL
];

@Injectable({
    providedIn: 'root'
})
export class ActivityRegistryService {
    private _activityConfigurations = new Map<string, IActivityConfiguration>();
    private _activityTypeNameToactivityLabelMapping = new Map<string, string>(); // key is activityType name, value is activity label (localized)

    constructor(private readonly _ecSvc: ExposureControlService) {
        this._populateKnownActivityTypes();
    }

    /**
     * Check if an activity is hidden due to the EC value or (absence of) a feature flag
     * @param activityConfiguration The activity configuration to check
     * @returns True if this activity should be hidden, false otherwise
     */
    public hidedByEc(activityConfiguration: IActivityConfiguration): boolean {
        switch (activityConfiguration.activityType) {
            case ScopeActivity.MODEL:
                return (!this._ecSvc.enableScopeActivity || __IS_A365__);
            case ValidationActivity.MODEL:
                return (!GlobalVariables.isPostGAFactory || (__IS_FABRIC_DPE__ && !GlobalVariables.enableFabricValidate));
            case DataflowRefreshActivity.MODEL:
                return (__IS_FABRIC_DPE__ ? false : !GlobalVariables.enableRefreshDataflowAdf);
            case TridentNotebookActivity.MODEL:
            case KqlActivity.MODEL:
                return !__IS_FABRIC_DPE__;
            case TridentSparkJobDefinitionActivity.MODEL:
                return !__IS_FABRIC_DPE__;
            case FabricApprovalActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && GlobalVariables.enableApprovalActivity);
            case AirflowActivity.MODEL:
                return !GlobalVariables.enableAirflowActivity;
            case InvokeCopyJobActivity.MODEL:
                return !__IS_FABRIC_DPE__;
            case InvokeDataBuildToolJobActivity.MODEL:
                return !__IS_FABRIC_DPE__;
            case InvokeDataQualityActivity.MODEL:
                return !GlobalVariables.enableDataQualityActivity;
            case ApiIntegrationActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && 
                    (GlobalVariables.enableApiIntegrationActivity || this._ecSvc.enableApiIntegrationActivity));
            case FabricActionsActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && GlobalVariables.enableFabricActionsActivity);
            case BusinessActionsActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && GlobalVariables.enableBusinessActionsActivity);
            case KustoActivity.MODEL:
                return (!GlobalVariables.showKustoConnector);
            case WebHookActivity.MODEL:
                return (!GlobalVariables.isPostGAFactory);
            case AzureHDInsightActivity.MODEL:
                return !__IS_FABRIC_DPE__ || !this._ecSvc.enableHDInsightActivity;
            case SwitchActivity.MODEL:
                return (!GlobalVariables.isPostGAFactory);
            case AzureMLExecutePipelineActivity.MODEL:
                return (!GlobalVariables.isPostGAFactory);
            case SynapseScopeJobDefinitionActivity.MODEL:
                return (!__IS_A365__ || !ScopeOnSynapseUtils.isSosEnabled());
            case MicrosoftTeamsActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && GlobalVariables.microsoftTeams)
            case Office365EmailActivity.MODEL:
                return !(__IS_FABRIC_DPE__ && GlobalVariables.office365Email);
            case EmailActivity.MODEL:
            case TeamsActivity.MODEL:
            case AzureBlobStorageActivity.MODEL:
            case AzureDevOpsActivity.MODEL:
            case AzureKeyVaultActivity.MODEL:
            case InvokeADFPipelineActivity.MODEL: {
                // The enableExtensibleActivity flag will enable all of these by default
                const activityType = activityConfiguration.activityType;
                if (GlobalVariables.hideLegacyTeamsOutlookActivities
                    && (activityType === EmailActivity.MODEL || activityType === TeamsActivity.MODEL)) {
                    return true;
                }
                if (GlobalVariables.enableExtensibleActivity) {
                    return false;
                }
                if (activityType === EmailActivity.MODEL) {
                    return (__IS_FABRIC_DPE__ ? false : !GlobalVariables.enableEmailActivityWithFF);
                }
                if (activityType === TeamsActivity.MODEL) {
                    return (__IS_FABRIC_DPE__ ? false : !GlobalVariables.enableTeamsActivityWithFF);
                }
                if (activityType === AzureBlobStorageActivity.MODEL && !GlobalVariables.enableLogActivity) {
                    return true;
                }
                if (activityType === AzureDevOpsActivity.MODEL && !GlobalVariables.enableDevOpsActivity) {
                    return true;
                }
                if (activityType === InvokeADFPipelineActivity.MODEL && !GlobalVariables.enableMoreActivities) {
                    return true;
                }
                if (activityType === AzureKeyVaultActivity.MODEL && !GlobalVariables.enableAKV) {
                    return true;
                }
                break;
            }
            case NotebookActivity.MODEL:
                return __IS_FABRIC_DPE__ && !this._ecSvc.enableDatabricksActivity;
            case SparkJarActivity.MODEL:
                return __IS_FABRIC_DPE__;
            case SparkPythonActivity.MODEL: {
                return __IS_FABRIC_DPE__;
            }
            case JobActivity.MODEL:
                return !this._ecSvc.enableAzureDatabricksADFJobActivity;
            case SemanticModelRefreshActivity.MODEL:
                return !__IS_FABRIC_DPE__;
            case InvokeSSISPackageActivity.MODEL:
                return !__IS_FABRIC_DPE__ || !GlobalVariables.enableSSISInFabric;
            case ExecutePipelineActivityDuplicate.MODEL:
                return !__IS_FABRIC_DPE__;
            case LakehouseMaintenanceActivity.MODEL:
                return !GlobalVariables.enableLakehouseMaintenanceActivity;
            case RefreshSqlEndpointActivity.MODEL:
                return !GlobalVariables.enableRefreshSqlEndpointActivity;
            case RefreshMaterializedLakeViewActivity.MODEL:
                return !GlobalVariables.enableRefreshMaterializedLakeViewActivity;
            default:                
                break;
        }
        return false;
    }

    public getAllActivityConfigurations(): IActivityConfiguration[] {
        const allActivityConfigurations: IActivityConfiguration[] = [];

        this._activityConfigurations.forEach((activityConfiguration) => {
            if (!this.hidedByEc(activityConfiguration)) {
                this._updateActivityConfiguration(activityConfiguration);
                allActivityConfigurations.push(activityConfiguration);
            }
        }
        );
        return allActivityConfigurations;
    }

    public getActivityConfiguration(activityType: ActivityType): IActivityConfiguration {
        const configuration = this._activityConfigurations.get(activityType.name);
        if (configuration) {
            return configuration;
        }
        throw new Error(`${activityType.name} not found in activity-registery`);
    }

    public registerActivityConfiguration(activityConfiguration: IActivityConfiguration): void {
        this._activityConfigurations.set(activityConfiguration.activityType.name, activityConfiguration);
    }

    public getLocalizedActivityTypeName(activityTypeName: string): string {
        if (this._activityTypeNameToactivityLabelMapping.has(activityTypeName)) {
            return this._activityTypeNameToactivityLabelMapping.get(activityTypeName);
        }
        return activityTypeName;
    }

    public getActivityIconClass(activityType: string): string {
        if (activityType) {
            const model = Metamodel.modelByName(activityType);
            if (!!model) {
                const config = this.getActivityConfiguration(model);
                return config.iconClass ?? '';
            }
        }
        return '';
    }

    public getActivityConfigurationByTypeName(activityTypeName: string): IActivityConfiguration {
        return this._activityConfigurations.get(activityTypeName);
    }

    private _populateKnownActivityTypes(): void {
        ActivityMetaModel.activityConfigurations.forEach((activityConfiguration) => {
            this.registerActivityConfiguration(activityConfiguration);
            this._configureActivityCategoriesForTrident(activityConfiguration);
            this._activityTypeNameToactivityLabelMapping.set(activityConfiguration.activityType.name, activityConfiguration.label);
        });
    }

    private _updateActivityConfiguration(config: IActivityConfiguration): void {
        if (GlobalVariables.office365Email && config instanceof EmailActivityConfiguration) {
            config.label = stringResources.LegacyEmailActivityLabel;
            config.iconClass = 'svg-i_Email_gray_16x16';
        } else if (GlobalVariables.microsoftTeams && config instanceof TeamsActivityConfiguration) {
            config.label = stringResources.LegacyTeamsActivityLabel;
            config.iconClass = 'svg-i_Teams_gray_16x16';
        } else if (__IS_FABRIC_DPE__ && config instanceof ExecutePipelineActivityConfiguration
            && !(config instanceof ExecutePipelineActivityDuplicateConfiguration)) {
            config.label = stringResources.LegacyExecutePipelineActivityLabel;
            config.iconClass = 'svg-i_execute_pipeline_gray_16x16'
        }
    }

    /*
    * For Trident, we want to maintain categories that are separate from ADF
    * This helps in clarity with regards to the floating activity menu
    * Note that we could do this in each respective activity configuration, but then we would have to add __IS_FABRIC_DPE__ into every single config which is messy
    */
    private _configureActivityCategoriesForTrident(config: IActivityConfiguration): void {
        if (__IS_FABRIC_DPE__ && config.supportedInTrident) {
            switch (config.activityType) {
                //    Move and Transform
                case CopyActivity.MODEL:
                case DataflowRefreshActivity.MODEL:
                case DeleteActivity.MODEL:
                case InvokeSSISPackageActivity.MODEL:
                case InvokeDataBuildToolJobActivity.MODEL:
                case InvokeDataQualityActivity.MODEL:
                case InvokeCopyJobActivity.MODEL:
                    config.categories = [stringResources.Activity_Category_DataFlow];
                    break;
                //    Metadata and Vali
                case LookupActivity.MODEL:
                case GetMetadataActivity.MODEL:
                case ValidationActivity.MODEL:
                    config.categories = [stringResources.Trident_Activity_Category_Metadata];
                    break;
                //    Control flow
                case IfConditionActivity.MODEL:
                case SwitchActivity.MODEL:
                case FilterActivity.MODEL:
                case WaitActivity.MODEL:
                case ForEachActivity.MODEL:
                case UntilActivity.MODEL:
                case SetVariableActivity.MODEL:
                case AppendVariableActivity.MODEL:
                case FabricApprovalActivity.MODEL:
                case FailActivity.MODEL:
                    config.categories = [stringResources.FeaturedTutorialsControlFlow];
                    break;
                //    Orchestrate
                case ExecutePipelineActivity.MODEL:
                case ExecutePipelineActivityDuplicate.MODEL:
                case WebActivity.MODEL:
                case AirflowActivity.MODEL:
                case WebHookActivity.MODEL:
                case SemanticModelRefreshActivity.MODEL:
                case AzureBlobStorageActivity.MODEL:
                case NotebookActivity.MODEL:
                case SparkJarActivity.MODEL:
                case SparkPythonActivity.MODEL:
                case JobActivity.MODEL:
                case AzureFunctionActivity.MODEL:
                case AzureHDInsightActivity.MODEL:
                case CustomActivity.MODEL:
                case AzureDevOpsActivity.MODEL:
                case RefreshSqlEndpointActivity.MODEL:
                case ApiIntegrationActivity.MODEL:
                case FabricActionsActivity.MODEL:
                case BusinessActionsActivity.MODEL:
                    config.categories = [stringResources.ADFHomeOrchestrateTitle];
                    break;
                //    Notifications
                case MicrosoftTeamsActivity.MODEL:
                case Office365EmailActivity.MODEL:
                    config.categories = [stringResources.Notifications];
                    break;
                //    Transform
                case TridentNotebookActivity.MODEL:
                case ScriptActivity.MODEL:
                case StoredProcedureActivity.MODEL:
                case ScopeActivity.MODEL:
                case KqlActivity.MODEL:
                case TridentSparkJobDefinitionActivity.MODEL:
                    config.categories = [stringResources.TransformLabel];
                    break;
                case LakehouseMaintenanceActivity.MODEL:
                case RefreshMaterializedLakeViewActivity.MODEL:
                    config.categories = [stringResources.Lakehouse_DisplayText];
                    break;
                //    Machine Learning
                case AzureMLExecutePipelineActivity.MODEL:
                    config.categories = [stringResources.Activity_Category_MachineLearning];
                    break;
                default:
                    break;
            }
        }
    }
}
